import { Container, Sprite } from 'pixi.js';
import type { InputController } from '../input/InputController';
import { BUILDING_DEFS, xOf, yOf } from '../sim/constants';
import { homeDustLevel } from '../sim/systems/families';
import type { SimView } from '../sim/view';
import { FLAG_CABLE, FLAG_PAVED, FLAG_PIPE, FLAG_ROAD } from '../sim/types';
import { hasFlag } from '../sim/world/terrain';
import type { GrayboxTextures } from './graybox';
import { HALF_W, tileTopY, vertexX, vertexY, type PickedTile } from './iso';
import { DESIGNATION_TINT, GHOST_INVALID, GHOST_VALID } from './tint';

const ROUTE_TINTS = { road: 0xcbbd96, pipe: 0x6db3dd, cable: 0xe8c95a, paved: 0xb8b8b0, reclaim: 0x9fd08a } as const;
const ROUTE_FLAGS = { road: FLAG_ROAD, pipe: FLAG_PIPE, cable: FLAG_CABLE, paved: FLAG_PAVED } as const;
/** Queued-deepening ramp: one level pending, two, three-plus. */
const ORDER_DEPTH_TINTS = [DESIGNATION_TINT, 0xb35f13, 0x8a4308] as const;

/** Cursor highlight, dig designations, drag previews, and the building ghost. */
export class OverlayLayer {
  readonly container = new Container();
  private readonly tex: GrayboxTextures;
  private readonly cursor: Sprite;
  private readonly ghost: Sprite;
  private readonly diamonds: Sprite[] = [];
  private lastStamp = '';

  constructor(tex: GrayboxTextures) {
    this.tex = tex;
    this.cursor = new Sprite(tex.diamondOutline);
    this.cursor.visible = false;
    this.ghost = new Sprite();
    this.ghost.visible = false;
    this.ghost.alpha = 0.55;
    this.container.addChild(this.ghost);
    this.container.addChild(this.cursor);
  }

  update(view: SimView, input: InputController): void {
    const state = view.state;
    const hover: PickedTile | null = input.hover;

    if (hover) {
      this.cursor.visible = true;
      this.cursor.position.set((hover.x - hover.y) * HALF_W - HALF_W, tileTopY(hover.x, hover.y, hover.z));
    } else {
      this.cursor.visible = false;
    }

    // Building ghost.
    let ghostTiles: boolean[] | null = null;
    let ghostDef: { w: number; h: number } | null = null;
    let ghostCorridor: { col: number; ok: boolean }[] | null = null;
    if (input.tool === 'place' && input.placeType && hover) {
      const type = input.placeType;
      const def = BUILDING_DEFS[type];
      const entry = this.tex.building(type);
      const check = view.canPlace(type, hover.x, hover.y);
      ghostCorridor = check.corridor ?? null;
      this.ghost.texture = entry.texture;
      this.ghost.position.set(vertexX(hover.x, hover.y) + entry.offsetX, vertexY(hover.x, hover.y) + entry.offsetY);
      // Family homes warn amber when the site sits in pit dust.
      const dusty = (def.familyHomes ?? 0) > 0 && homeDustLevel(view, hover.x, hover.y) > 0.25;
      this.ghost.tint = !check.ok ? GHOST_INVALID : dusty ? 0xe0a850 : 0xffffff;
      this.ghost.visible = true;
      ghostTiles = check.tiles;
      ghostDef = def;
    } else {
      this.ghost.visible = false;
    }

    // Pooled diamonds: orders + pending drag + ghost footprint validity.
    // Orders hash by content (col/remaining/suspended), not size — suspension
    // flips and same-size rect slides must repaint too.
    let ordersHash = 0;
    for (const [col, order] of state.orders) {
      const remaining = order.targetDepth - (state.terrain.depth[col] ?? 0);
      ordersHash = (ordersHash * 33 + col * 131 + remaining * 7 + (order.suspended ? 1 : 0)) | 0;
    }
    const affordable = input.erasing || input.pendingCostCents() <= state.economy.cashCents;
    const stamp = `${ordersHash}:${state.terrain.version}:${input.pendingVersion}:${input.tool}:${hover ? hover.x + ':' + hover.y : ''}:${input.erasing ? 1 : 0}:${input.routeBlocked ? 1 : 0}:${affordable ? 1 : 0}`;
    if (stamp === this.lastStamp) return;
    this.lastStamp = stamp;

    let used = 0;
    const place = (x: number, y: number, depth: number, tint: number, alpha: number): void => {
      let sprite = this.diamonds[used];
      if (!sprite) {
        sprite = new Sprite(this.tex.diamondFill);
        this.diamonds.push(sprite);
        this.container.addChild(sprite);
      }
      sprite.visible = true;
      sprite.position.set((x - y) * HALF_W - HALF_W, tileTopY(x, y, depth));
      sprite.tint = tint;
      sprite.alpha = alpha;
      used += 1;
    };

    for (const [col, order] of state.orders) {
      const x = xOf(col);
      const y = yOf(col);
      const depth = state.terrain.depth[col] ?? 0;
      const remaining = Math.max(1, order.targetDepth - depth);
      const tint = order.suspended
        ? 0x999999
        : ORDER_DEPTH_TINTS[Math.min(remaining, ORDER_DEPTH_TINTS.length) - 1]!;
      place(x, y, depth, tint, 0.35);
    }
    if (input.pendingCols.size > 0) {
      const routeFlag: number = ROUTE_FLAGS[input.tool as keyof typeof ROUTE_FLAGS] ?? 0;
      const baseTint =
        input.tool === 'road'
          ? ROUTE_TINTS.road
          : input.tool === 'pipe'
            ? ROUTE_TINTS.pipe
            : input.tool === 'cable'
              ? ROUTE_TINTS.cable
              : input.tool === 'paved'
                ? ROUTE_TINTS.paved
                : input.tool === 'reclaim'
                  ? ROUTE_TINTS.reclaim
                  : DESIGNATION_TINT;
      // Erase strokes, blocked routes, and unaffordable runs all read red.
      const problem = input.erasing || input.routeBlocked || !affordable;
      for (const col of input.pendingCols) {
        if (state.orders.has(col)) continue;
        const existing = routeFlag !== 0 && hasFlag(state.terrain, col, routeFlag);
        place(
          xOf(col),
          yOf(col),
          state.terrain.depth[col] ?? 0,
          problem ? GHOST_INVALID : baseTint,
          // Route tiles that already exist ride along dimmed — free segments.
          existing && !input.erasing ? 0.3 : 0.55,
        );
      }
    }
    if (ghostTiles && ghostDef && hover) {
      let index = 0;
      for (let dy = 0; dy < ghostDef.h; dy += 1) {
        for (let dx = 0; dx < ghostDef.w; dx += 1) {
          const ok = ghostTiles[index] ?? false;
          place(hover.x + dx, hover.y + dy, hover.z, ok ? GHOST_VALID : GHOST_INVALID, 0.4);
          index += 1;
        }
      }
    }
    // Rail depots also preview their right-of-way to the map edge.
    if (ghostCorridor) {
      for (const { col, ok } of ghostCorridor) {
        place(xOf(col), yOf(col), state.terrain.depth[col] ?? 0, ok ? GHOST_VALID : GHOST_INVALID, 0.28);
      }
    }
    for (let index = used; index < this.diamonds.length; index += 1) {
      this.diamonds[index]!.visible = false;
    }
  }
}
